import { Component, OnInit } from "@angular/core";
import { GroupedChartRow, GroupedChartSeries, InfoCards, RetentionSegment, retentionTooltipIcons } from "../../../_models";
import { CardState, DoughnutChartData, InlineTableData, PerfTableColumn, retentionValueCols } from "../../data-visualization";
import { ProductViewType } from "../../orders/orders.data";
import {
  canceledWithPendingOrderData,
  subscriptionBillingCycleData,
  subscriptionCampaignData,
  subscriptionCancellationReasons,
  subscriptionCustomerStageData,
  subscriptionDoughnutData,
  subscriptionFrequencyData,
  subscriptionOfferTypeData,
  subscriptionProductData,
  subscriptionSummaryTableData,
  subscriptionTopCards,
} from "../../subscription/subscription.data";

@Component({
  selector: "app-subscription-report",
  templateUrl: "./subscription-report.component.html",
  styleUrls: ["./../../dashboard-section-v2.scss", "./../dashboard-report.component.scss"],
})
export class DashboardSubscriptionReportComponent implements OnInit {
  topCards: InfoCards = subscriptionTopCards;
  doughnutData: DoughnutChartData = subscriptionDoughnutData;
  summaryTableData: InlineTableData = subscriptionSummaryTableData;
  cancellationReasons = subscriptionCancellationReasons;
  offerTypeData = subscriptionOfferTypeData;
  productData = subscriptionProductData;
  billingCycleData = subscriptionBillingCycleData;
  frequencyData = subscriptionFrequencyData;
  campaignData = subscriptionCampaignData;
  customerStageData = subscriptionCustomerStageData;
  canceledWithPendingOrderData = canceledWithPendingOrderData;
  retentionTooltipIcons = retentionTooltipIcons;

  // The three stacked bar charts are unreadable at report width - print them as tables.
  cancellationState: CardState<any> = { data: [], loading: false, noData: true };
  billingCycleState: CardState<any> = { data: [], loading: false, noData: true };
  frequencyState: CardState<any> = { data: [], loading: false, noData: true };
  stackedValueCols: PerfTableColumn[] = [
    { label: "Requests", field: "requests" },
    { label: "Saved", field: "saved" },
    { label: "Cancelled", field: "cancelled" },
    { label: "Abandoned", field: "abandoned" },
  ];
  cancellationNameCol: PerfTableColumn = { label: "Reason", field: "name" };
  billingCycleNameCol: PerfTableColumn = { label: "Billing Cycle", field: "name" };
  frequencyNameCol: PerfTableColumn = { label: "Frequency", field: "name" };

  offerTypeNameCol: PerfTableColumn = { label: "Offer Type", field: "name" };
  offerTypeValueCols = retentionValueCols;
  productNameCol: PerfTableColumn = { label: "Product Name", field: "name" };
  productValueCols = retentionValueCols;
  campaignNameCol: PerfTableColumn = { label: "Name", field: "name" };
  campaignValueCols = retentionValueCols;
  customerStageNameCol: PerfTableColumn = { label: "Name", field: "name" };
  customerStageValueCols = retentionValueCols;

  constructor() {}

  get showCanceledWithPendingOrder(): boolean {
    return this.canceledWithPendingOrderData.show;
  }

  get canceledWithPendingOrderMetrics(): { label: string; value: string }[] {
    const data = this.canceledWithPendingOrderData;
    return [
      { label: "Quantity", value: data.quantity },
      { label: "Subscription Value Lost", value: data.subscriptionValueLost },
      { label: "Cancellation Rate", value: data.cancellationRate },
      { label: "Cycle 1 / Initial", value: data.cycle1Quantity + " / " + data.cycle1Value },
      { label: "Cycle 2+ / Rebill", value: data.cycle2Quantity + " / " + data.cycle2Value },
      { label: "Avg. Value", value: data.avgValue },
    ];
  }

  // No toggle in print - label whichever view is selected.
  get productViewLabel(): string {
    let label = "Base Product";
    if (this.productData.type === ProductViewType.Campaign) {
      label = "Campaign Product";
    }
    return label;
  }

  ngOnInit() {
    this.cancellationState = this.flattenStackedData(this.cancellationReasons);
    this.billingCycleState = this.flattenStackedData(this.billingCycleData);
    this.frequencyState = this.flattenStackedData(this.frequencyData);
  }

  // The three grouped charts share one shape, and their numbers are raw while the other tables are formatted.
  flattenStackedData(source: CardState<GroupedChartRow>): CardState<any> {
    const rows = source.data.map((row: GroupedChartRow) => {
      const mapped = {
        name: row.name,
        requests: (row.requests || 0).toLocaleString(),
        saved: "0",
        cancelled: "0",
        abandoned: "0",
      };
      row.series.forEach((entry: GroupedChartSeries) => {
        const value = (entry.value || 0).toLocaleString();
        if (entry.name === RetentionSegment.Saved) {
          mapped.saved = value;
        } else if (entry.name === RetentionSegment.Cancelled) {
          mapped.cancelled = value;
        } else if (entry.name === RetentionSegment.Abandoned) {
          mapped.abandoned = value;
        }
      });
      return mapped;
    });

    return { data: rows, loading: source.loading, noData: source.noData };
  }
}
